import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, API_BASE, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from "@/components/ui/dialog";
import { Upload, DownloadSimple, CheckCircle, WarningCircle, ArrowUpRight } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Reusable bulk-import trigger.
 *
 * <BulkImportButton entity="customers" label="Import CSV" onDone={() => reload()} />
 *
 * `entity` must match a key registered in backend routers/bulk_import.py
 *   customers | suppliers | products | services | dsc | credentials
 */
export default function BulkImportButton({ entity, label = "Import", onDone, testid }) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);         // {total,valid,invalid,errors,sample}
  const [busy, setBusy] = useState(false);
  const [phase, setPhase] = useState("choose");         // choose | previewing | preview | importing | done
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  const reset = () => {
    setFile(null); setPreview(null); setResult(null); setPhase("choose"); setBusy(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };
  useEffect(() => { if (!open) reset(); }, [open]);

  const templateUrl = useMemo(
    () => `${API_BASE}/imports/${entity}/template.csv`,
    [entity]
  );

  const runPreview = async (selected) => {
    if (!selected) return;
    setBusy(true); setPhase("previewing");
    try {
      const fd = new FormData(); fd.append("file", selected);
      const { data } = await api.post(`/imports/${entity}/preview`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data); setPhase("preview");
    } catch (err) {
      toast.error(formatApiError(err));
      setPhase("choose");
    } finally { setBusy(false); }
  };

  const runExecute = async () => {
    if (!file) return;
    setBusy(true); setPhase("importing");
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post(`/imports/${entity}/execute`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data); setPhase("done");
      if (data.inserted > 0) toast.success(`${data.inserted} rows imported`);
      if (data.failed > 0) toast.error(`${data.failed} rows failed`);
      onDone?.(data);
    } catch (err) {
      toast.error(formatApiError(err));
      setPhase("preview");
    } finally { setBusy(false); }
  };

  const downloadTemplate = async () => {
    try {
      // Use axios to inherit auth + interceptors; then trigger a browser download
      const res = await api.get(`/imports/${entity}/template.csv`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `${entity}-template.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      // Fallback: open direct URL (browser will still authenticate via cookie if set)
      window.open(templateUrl, "_blank");
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button
        type="button"
        variant="outline"
        className="h-9"
        onClick={() => setOpen(true)}
        data-testid={testid || `bulk-import-${entity}-btn`}
      >
        <Upload size={15} weight="bold" className="mr-1.5" /> {label}
      </Button>

      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight capitalize">
            Bulk Import · {entity.replace("-", " ")}
          </DialogTitle>
          <DialogDescription>
            Upload a CSV or XLSX file. Download the template first — column order &amp; headers must match.
          </DialogDescription>
        </DialogHeader>

        {phase === "choose" && (
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <Button variant="secondary" onClick={downloadTemplate} data-testid={`bulk-tpl-${entity}`}>
                <DownloadSimple size={15} className="mr-1.5" /> Download template
              </Button>
              <span className="text-xs text-muted-foreground">
                Required fields are marked with placeholders like <code>&lt;name&gt;</code>.
              </span>
            </div>
            <div className="rounded-md border-2 border-dashed p-6 text-center">
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx,.txt"
                className="hidden"
                data-testid={`bulk-file-${entity}`}
                onChange={(e) => {
                  const f = e.target.files?.[0] || null;
                  setFile(f);
                  if (f) runPreview(f);
                }}
              />
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={busy}
                data-testid={`bulk-choose-${entity}`}
              >
                <Upload size={15} className="mr-1.5" /> Choose file
              </Button>
              <p className="mt-2 text-xs text-muted-foreground">CSV or XLSX · max ~5 MB</p>
            </div>
          </div>
        )}

        {phase === "previewing" && (
          <div className="py-8 text-center text-muted-foreground">Analysing file…</div>
        )}

        {phase === "preview" && preview && (
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <StatCard label="Total rows" value={preview.total} />
              <StatCard label="Valid" value={preview.valid} accent="ok" />
              <StatCard label="Errors" value={preview.invalid} accent={preview.invalid ? "err" : "muted"} />
            </div>

            {preview.errors?.length > 0 && (
              <div className="rounded-md border p-2 max-h-56 overflow-auto">
                <div className="text-xs uppercase tracking-wide text-muted-foreground px-1 pb-2">
                  Errors (showing first {preview.errors.length})
                </div>
                <table className="w-full text-[12.5px]">
                  <thead className="text-left text-muted-foreground">
                    <tr><th className="px-1 py-1">Row</th><th className="px-1 py-1">Error</th></tr>
                  </thead>
                  <tbody>
                    {preview.errors.map((e) => (
                      <tr key={e.row} className="border-t">
                        <td className="px-1 py-1 font-mono">{e.row}</td>
                        <td className="px-1 py-1 text-destructive">{e.error}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>{preview.valid} rows will be imported. Errors are skipped.</span>
              <Button variant="ghost" size="sm" onClick={reset}>Choose a different file</Button>
            </div>
          </div>
        )}

        {phase === "importing" && (
          <div className="py-8 text-center text-muted-foreground">Importing…</div>
        )}

        {phase === "done" && result && (
          <div className="space-y-3">
            <div className="grid grid-cols-4 gap-3">
              <StatCard label="Total" value={result.total} />
              <StatCard label="Inserted" value={result.inserted || 0} accent="ok" icon="ok" />
              <StatCard label="Updated" value={result.updated || 0} accent={result.updated ? "ok" : "muted"} />
              <StatCard label="Failed" value={result.failed} accent={result.failed ? "err" : "muted"} icon={result.failed ? "err" : null} />
            </div>
            {result.errors?.length > 0 && (
              <div className="rounded-md border p-2 max-h-56 overflow-auto">
                <div className="text-xs uppercase tracking-wide text-muted-foreground px-1 pb-2">Failed rows</div>
                <table className="w-full text-[12.5px]">
                  <thead className="text-left text-muted-foreground">
                    <tr><th className="px-1 py-1">Row</th><th className="px-1 py-1">Error</th></tr>
                  </thead>
                  <tbody>
                    {result.errors.map((e) => (
                      <tr key={e.row} className="border-t">
                        <td className="px-1 py-1 font-mono">{e.row}</td>
                        <td className="px-1 py-1 text-destructive">{e.error}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {phase === "preview" && (
            <>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button
                onClick={runExecute}
                disabled={busy || !preview?.valid}
                data-testid={`bulk-import-run-${entity}`}
              >
                Import {preview?.valid || 0} rows <ArrowUpRight size={14} className="ml-1" />
              </Button>
            </>
          )}
          {phase === "done" && (
            <Button onClick={() => setOpen(false)} data-testid={`bulk-close-${entity}`}>Close</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StatCard({ label, value, accent = "muted", icon }) {
  const tone = {
    ok: "text-emerald-600 border-emerald-200 bg-emerald-50",
    err: "text-destructive border-destructive/30 bg-destructive/5",
    muted: "text-foreground border-muted bg-muted/30",
  }[accent];
  return (
    <div className={`rounded-md border px-3 py-2 ${tone}`}>
      <div className="text-[11px] uppercase tracking-wide opacity-70 flex items-center gap-1">
        {icon === "ok" && <CheckCircle size={12} weight="fill" />}
        {icon === "err" && <WarningCircle size={12} weight="fill" />}
        {label}
      </div>
      <div className="text-xl font-heading font-bold leading-tight">{value}</div>
    </div>
  );
}
